import React, { useEffect, useState, useMemo } from 'react';
import {
    AlertTriangle,
    CheckCircle,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    FileText,
    Map,
    Settings,
    Upload,
    X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useSnackbar } from 'notistack';
import httpClient from '../../api/httpClient';
import { useActiveLeadStatuses } from '../../api/lead-statuses/use-lead-statuses';
import { useUsers } from '@/api/users/use-users.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomAttributeDefinitionsManagement } from '../../hooks/useCustomAttributes';
import LeadFieldMappingTable from './mapping/LeadFieldMappingTable';
import LeadDefaultsStep, {
    NO_DEFAULT_ASSIGNEE_VALUE,
    NO_DEFAULT_STATUS_VALUE,
} from './mapping/LeadDefaultsStep';
import { validateLeadFieldMapping } from './mapping/mappingValidation';

interface CsvAnalysis {
    headers: string[];
    preview_rows: Record<string, any>[];
    total_rows: number;
    available_fields: Record<string, any>;
    suggested_mappings: Record<string, string | null>;
    required_fields: string[];
}

interface ColumnMapping {
    [csvColumn: string]: string | null;
}

interface IgnoredDuplicate {
    row: number;
    matched_by: string;
    message: string;
}

interface ImportResults {
    imported: number;
    ignored: number;
    ignored_duplicates?: IgnoredDuplicate[];
    failed: number;
    errors?: string[];
    total_processed: number;
}

interface EnhancedImportWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onImportComplete: (result: ImportResults) => void;
}

const EnhancedImportWizard: React.FC<EnhancedImportWizardProps> = ({
    isOpen,
    onClose,
    onImportComplete,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { data: leadStatusesData } = useActiveLeadStatuses();
    const { data: usersData } = useUsers();

    // Fetch custom attribute definitions for import mapping
    const { definitions: customAttributeDefinitions } = useCustomAttributeDefinitionsManagement();

    const [currentStep, setCurrentStep] = useState(1);
    const [csvAnalysis, setCsvAnalysis] = useState<CsvAnalysis | null>(null);
    const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
    const [skipFirstRow, setSkipFirstRow] = useState(true);
    const [selectedLeadStatus, setSelectedLeadStatus] = useState<string>(NO_DEFAULT_STATUS_VALUE);
    const [selectedAssignee, setSelectedAssignee] = useState<string>(NO_DEFAULT_ASSIGNEE_VALUE);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importResults, setImportResults] = useState<ImportResults | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Reset function to clear all state
    const resetWizard = () => {
        setCurrentStep(1);
        setCsvAnalysis(null);
        setColumnMapping({});
        setSkipFirstRow(true);
        setSelectedLeadStatus(NO_DEFAULT_STATUS_VALUE);
        setSelectedAssignee(NO_DEFAULT_ASSIGNEE_VALUE);
        setIsAnalyzing(false);
        setIsImporting(false);
        setImportResults(null);
        setFile(null);
        setIsDragOver(false);
    };

    // Analyze CSV when file is provided
    useEffect(() => {
        if (file) {
            analyzeCsv();
        }
    }, [file]);

    const analyzeCsv = async () => {
        if (!file) return;

        setIsAnalyzing(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await httpClient.post('/crm/leads/analyze-csv', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setCsvAnalysis(data.data);

            // Initialize column mapping with suggestions
            const initialMapping: ColumnMapping = {};
            Object.entries(data.data.suggested_mappings).forEach(([csvColumn, suggestedField]) => {
                initialMapping[csvColumn] = suggestedField as string | null;
            });
            setColumnMapping(initialMapping);

            setCurrentStep(2);
        } catch (error) {
            enqueueSnackbar('Failed to analyze CSV file', { variant: 'error' });
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleColumnMappingChange = (csvColumn: string, leadField: string | null) => {
        setColumnMapping(prev => ({
            ...prev,
            [csvColumn]: leadField,
        }));
    };

    // Memoized validation to ensure reactivity
    const validation = useMemo(() => {
        if (!csvAnalysis) {
            return {
                isValid: false,
                errors: ['No CSV analysis available'],
                warnings: [],
                missingRequired: [],
            };
        }

        return validateLeadFieldMapping(
            columnMapping,
            csvAnalysis.available_fields,
            csvAnalysis.required_fields,
            csvAnalysis.headers.length
        );
    }, [csvAnalysis, columnMapping]);

    const validateMapping = () => validation;

    const handleImport = async () => {
        const validation = validateMapping();
        if (!validation.isValid) {
            validation.errors.forEach(error => {
                enqueueSnackbar(error, { variant: 'error' });
            });
            return;
        }

        setIsImporting(true);
        const formData = new FormData();
        formData.append('file', file!);

        // Send column mapping as associative array (CSV column -> Lead field)
        // Filter out unmapped columns (those mapped to '__no_mapping__')
        const cleanColumnMapping: { [key: string]: string } = {};
        Object.entries(columnMapping).forEach(([csvCol, leadField]) => {
            if (leadField && leadField !== '__no_mapping__') {
                cleanColumnMapping[csvCol] = leadField;
            }
        });

        // Append each column mapping as separate form fields
        Object.entries(cleanColumnMapping).forEach(([csvCol, leadField]) => {
            formData.append(`column_mapping[${csvCol}]`, leadField);
        });

        formData.append('skip_first_row', skipFirstRow ? '1' : '0');

        // Add lead status and assignee if selected
        if (selectedLeadStatus && selectedLeadStatus !== '__no_status__') {
            formData.append('lead_status', selectedLeadStatus);
        }
        if (selectedAssignee && selectedAssignee !== '__no_assignee__') {
            formData.append('assignee_id', selectedAssignee);
        }

        try {
            const { data } = await httpClient.post('/crm/leads/enhanced-import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Invalidate leads query to refresh the list
            queryClient.invalidateQueries({ queryKey: ['leads'] });

            onImportComplete(data.data);
            const ignoredCount = data.data.ignored ?? 0;
            enqueueSnackbar(
                `Import completed. ${data.data.imported} imported, ${ignoredCount} duplicate${ignoredCount === 1 ? '' : 's'} ignored, ${data.data.failed} failed.`,
                { variant: 'success' }
            );

            // Store results and show results step instead of closing
            setImportResults(data.data);
            setCurrentStep(5);
        } catch (error) {
            enqueueSnackbar('Failed to import leads', { variant: 'error' });
        } finally {
            setIsImporting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <FileText className="h-6 w-6 text-blue-600" />
                        <div>
                            <h2 className="text-xl font-semibold">Lead Import</h2>
                            <p className="text-sm text-muted-foreground">
                                Import leads with custom column mapping
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            resetWizard();
                            onClose();
                        }}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Progress Steps */}
                <div className="flex items-center justify-center p-4 border-b bg-muted/50 flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <div
                            className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-blue-600' : 'text-muted-foreground'}`}
                        >
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                    currentStep >= 1
                                        ? 'border-blue-600 bg-blue-600 text-white'
                                        : 'border-border'
                                }`}
                            >
                                {currentStep > 1 ? <CheckCircle2 className="h-4 w-4" /> : '1'}
                            </div>
                            <span className="text-sm font-medium">Upload & Analyze</span>
                        </div>

                        <ChevronRight className="h-4 w-4 text-muted-foreground" />

                        <div
                            className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-blue-600' : 'text-muted-foreground'}`}
                        >
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                    currentStep >= 2
                                        ? 'border-blue-600 bg-blue-600 text-white'
                                        : 'border-border'
                                }`}
                            >
                                {currentStep > 2 ? <CheckCircle2 className="h-4 w-4" /> : '2'}
                            </div>
                            <span className="text-sm font-medium">Map Columns</span>
                        </div>

                        <ChevronRight className="h-4 w-4 text-muted-foreground" />

                        <div
                            className={`flex items-center gap-2 ${currentStep >= 3 ? 'text-blue-600' : 'text-muted-foreground'}`}
                        >
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                    currentStep >= 3
                                        ? 'border-blue-600 bg-blue-600 text-white'
                                        : 'border-border'
                                }`}
                            >
                                {currentStep > 3 ? <CheckCircle2 className="h-4 w-4" /> : '3'}
                            </div>
                            <span className="text-sm font-medium">Import Options</span>
                        </div>

                        <ChevronRight className="h-4 w-4 text-muted-foreground" />

                        <div
                            className={`flex items-center gap-2 ${currentStep >= 4 ? 'text-blue-600' : 'text-muted-foreground'}`}
                        >
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                    currentStep >= 4
                                        ? 'border-blue-600 bg-blue-600 text-white'
                                        : 'border-border'
                                }`}
                            >
                                {currentStep > 4 ? <CheckCircle2 className="h-4 w-4" /> : '4'}
                            </div>
                            <span className="text-sm font-medium">Import</span>
                        </div>

                        <ChevronRight className="h-4 w-4 text-muted-foreground" />

                        <div
                            className={`flex items-center gap-2 ${currentStep >= 5 ? 'text-blue-600' : 'text-muted-foreground'}`}
                        >
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                    currentStep >= 5
                                        ? 'border-blue-600 bg-blue-600 text-white'
                                        : 'border-border'
                                }`}
                            >
                                {currentStep > 5 ? <CheckCircle2 className="h-4 w-4" /> : '5'}
                            </div>
                            <span className="text-sm font-medium">Results</span>
                        </div>
                    </div>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0">
                    {currentStep === 1 && (
                        <div className="space-y-6">
                            {/* File Upload */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Upload className="h-5 w-5" />
                                        Upload CSV File
                                    </CardTitle>
                                    <p className="text-sm text-muted-foreground">
                                        Select a CSV file to import leads. The file will be analyzed
                                        to detect columns and suggest field mappings.
                                    </p>
                                </CardHeader>
                                <CardContent>
                                    {!file ? (
                                        <div
                                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                                                isDragOver
                                                    ? 'border-blue-400 bg-blue-500/10'
                                                    : 'border-border hover:border-muted-foreground/50'
                                            }`}
                                            onDragOver={e => {
                                                e.preventDefault();
                                                setIsDragOver(true);
                                            }}
                                            onDragLeave={() => {
                                                setIsDragOver(false);
                                            }}
                                            onDrop={e => {
                                                e.preventDefault();
                                                setIsDragOver(false);
                                                const droppedFile = e.dataTransfer.files[0];
                                                if (
                                                    droppedFile &&
                                                    (droppedFile.type === 'text/csv' ||
                                                        droppedFile.type ===
                                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                                                        droppedFile.name.endsWith('.csv') ||
                                                        droppedFile.name.endsWith('.xlsx'))
                                                ) {
                                                    if (droppedFile.size > 10 * 1024 * 1024) {
                                                        // 10MB limit
                                                        enqueueSnackbar(
                                                            'File size must be less than 10MB',
                                                            {
                                                                variant: 'error',
                                                            }
                                                        );
                                                    } else {
                                                        setFile(droppedFile);
                                                    }
                                                } else {
                                                    enqueueSnackbar(
                                                        'Please select a valid CSV or XLSX file',
                                                        { variant: 'error' }
                                                    );
                                                }
                                            }}
                                        >
                                            <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                            <p className="text-lg font-medium text-foreground mb-2">
                                                Drop your CSV or XLSX file here
                                            </p>
                                            <p className="text-sm text-muted-foreground mb-4">
                                                or click to browse
                                            </p>
                                            <Button
                                                variant="outline"
                                                onClick={() =>
                                                    document.getElementById('file-input')?.click()
                                                }
                                            >
                                                Choose File
                                            </Button>
                                            <input
                                                id="file-input"
                                                type="file"
                                                accept=".csv,.xlsx"
                                                onChange={e => {
                                                    const selectedFile = e.target.files?.[0];
                                                    if (selectedFile) {
                                                        if (
                                                            selectedFile.type === 'text/csv' ||
                                                            selectedFile.type ===
                                                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                                                            selectedFile.name.endsWith('.csv') ||
                                                            selectedFile.name.endsWith('.xlsx')
                                                        ) {
                                                            if (
                                                                selectedFile.size >
                                                                10 * 1024 * 1024
                                                            ) {
                                                                // 10MB limit
                                                                enqueueSnackbar(
                                                                    'File size must be less than 10MB',
                                                                    {
                                                                        variant: 'error',
                                                                    }
                                                                );
                                                            } else {
                                                                setFile(selectedFile);
                                                            }
                                                        } else {
                                                            enqueueSnackbar(
                                                                'Please select a valid CSV or XLSX file',
                                                                {
                                                                    variant: 'error',
                                                                }
                                                            );
                                                        }
                                                    }
                                                }}
                                                className="hidden"
                                            />
                                            <p className="text-xs text-muted-foreground mt-2">
                                                Supported formats: CSV files up to 10MB
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <FileText className="h-8 w-8 text-green-600 dark:text-green-400" />
                                                    <div>
                                                        <p className="font-medium text-green-800 dark:text-green-300">
                                                            {file.name}
                                                        </p>
                                                        <p className="text-sm text-green-600 dark:text-green-400">
                                                            {(file.size / 1024).toFixed(1)} KB
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setFile(null)}
                                                    className="text-green-600 hover:text-green-700 hover:bg-green-500/10 dark:text-green-400 dark:hover:text-green-300"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>

                                            <Button
                                                onClick={analyzeCsv}
                                                disabled={isAnalyzing}
                                                className="w-full"
                                            >
                                                {isAnalyzing ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        Analyzing CSV...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FileText className="h-4 w-4 mr-2" />
                                                        Analyze CSV
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Analysis Status */}
                            {isAnalyzing && (
                                <Card>
                                    <CardContent className="pt-6">
                                        <div className="text-center space-y-4">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                                            <p className="text-lg font-medium">
                                                Analyzing CSV file...
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                Please wait while we analyze your CSV file structure
                                                and suggest field mappings.
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}

                    {currentStep === 2 && csvAnalysis && (
                        <div className="space-y-6">
                            {/* Import Options - Moved above CSV list */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Settings className="h-5 w-5" />
                                        Import Options
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="skip-first-row"
                                                checked={skipFirstRow}
                                                onCheckedChange={checked =>
                                                    setSkipFirstRow(checked as boolean)
                                                }
                                            />
                                            <Label htmlFor="skip-first-row">
                                                Skip first row (header)
                                            </Label>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* CSV Preview with Inline Mapping */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Map className="h-5 w-5" />
                                        Column Mapping & Preview ({
                                            csvAnalysis.preview_rows.length
                                        }{' '}
                                        of {csvAnalysis.total_rows} rows)
                                    </CardTitle>
                                    <p className="text-sm text-muted-foreground">
                                        Map your CSV columns to lead fields directly in the table.
                                        Required fields are marked with an asterisk (*). Select "No
                                        mapping" to skip unwanted columns.
                                    </p>
                                </CardHeader>
                                <CardContent>
                                    {/* Validation Summary */}
                                    <div className="mb-6">
                                        {validation.errors.length > 0 && (
                                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                                                <div className="flex items-center mb-2">
                                                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                                                    <h4 className="font-medium text-red-800 dark:text-red-300">
                                                        Mapping Issues
                                                    </h4>
                                                </div>
                                                <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                                                    {validation.errors.map((error, index) => (
                                                        <li key={index}>• {error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {validation.warnings.length > 0 && (
                                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                                                <div className="flex items-center mb-2">
                                                    <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                                                    <h4 className="font-medium text-yellow-800 dark:text-yellow-300">
                                                        Suggestions
                                                    </h4>
                                                </div>
                                                <ul className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
                                                    {validation.warnings.map((warning, index) => (
                                                        <li key={index}>• {warning}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {validation.isValid && (
                                            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                                                <div className="flex items-center">
                                                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                                                    <span className="font-medium text-green-800 dark:text-green-300">
                                                        All required fields are mapped!
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <LeadFieldMappingTable
                                        sourceFields={csvAnalysis.headers.map(header => ({
                                            key: header,
                                        }))}
                                        availableFields={csvAnalysis.available_fields}
                                        mapping={columnMapping}
                                        onChange={handleColumnMappingChange}
                                        suggestedMappings={csvAnalysis.suggested_mappings}
                                        previewRows={csvAnalysis.preview_rows}
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="space-y-6">
                            {/* Import Options */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Settings className="h-5 w-5" />
                                        Import Options
                                    </CardTitle>
                                    <p className="text-sm text-muted-foreground">
                                        Configure default values for imported leads
                                    </p>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-6">
                                        <LeadDefaultsStep
                                            value={{
                                                leadStatusId: selectedLeadStatus,
                                                assigneeId: selectedAssignee,
                                            }}
                                            onChange={defaults => {
                                                setSelectedLeadStatus(defaults.leadStatusId);
                                                setSelectedAssignee(defaults.assigneeId);
                                            }}
                                        />

                                        {/* Skip First Row Option */}
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="skip-first-row-step3"
                                                checked={skipFirstRow}
                                                onCheckedChange={checked =>
                                                    setSkipFirstRow(checked as boolean)
                                                }
                                            />
                                            <Label htmlFor="skip-first-row-step3">
                                                Skip first row (header)
                                            </Label>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Summary */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="h-5 w-5" />
                                        Import Summary
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Total rows to process:
                                            </span>
                                            <span className="font-medium">
                                                {csvAnalysis?.total_rows || 0}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Columns mapped:
                                            </span>
                                            <span className="font-medium">
                                                {
                                                    Object.values(columnMapping).filter(
                                                        field => field && field !== '__no_mapping__'
                                                    ).length
                                                }
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Columns mapped:
                                            </span>
                                            <span className="font-medium">
                                                {
                                                    Object.values(columnMapping).filter(
                                                        field => field && field !== '__no_mapping__'
                                                    ).length
                                                }
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Default status:
                                            </span>
                                            <span className="font-medium">
                                                {selectedLeadStatus &&
                                                selectedLeadStatus !== '__no_status__'
                                                    ? leadStatusesData?.data?.find(
                                                          s => s.id === selectedLeadStatus
                                                      )?.label || 'Unknown'
                                                    : 'System default'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Default assignee:
                                            </span>
                                            <span className="font-medium">
                                                {selectedAssignee &&
                                                selectedAssignee !== '__no_assignee__'
                                                    ? usersData?.data?.find(
                                                          u => u.id === selectedAssignee
                                                      )?.name || 'Unknown'
                                                    : 'None'}
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {currentStep === 4 && (
                        <div className="text-center py-12">
                            <div className="space-y-4">
                                <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
                                <p className="text-lg font-medium">Ready to Import</p>
                                <p className="text-sm text-muted-foreground">
                                    Review your column mapping and click "Start Import" to proceed
                                </p>

                                {/* Validation Summary */}
                                {(() => {
                                    const validation = validateMapping();
                                    return (
                                        <div className="mt-4">
                                            {validation.isValid ? (
                                                <div className="text-green-600 text-sm">
                                                    ✓ All required fields are mapped
                                                </div>
                                            ) : (
                                                <div className="text-red-600 text-sm">
                                                    ✗ {validation.errors.join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {currentStep === 5 && importResults && (
                        <div className="space-y-6">
                            {/* Import Results Summary */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <CheckCircle2 className="h-5 w-5" />
                                        Import Results
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {/* Summary Stats */}
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                            <div className="text-center p-4 bg-blue-500/10 rounded-lg">
                                                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                                    {importResults.total_processed}
                                                </div>
                                                <div className="text-sm text-blue-600 dark:text-blue-400">
                                                    Total Processed
                                                </div>
                                            </div>
                                            <div className="text-center p-4 bg-green-500/10 rounded-lg">
                                                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                                    {importResults.imported}
                                                </div>
                                                <div className="text-sm text-green-600 dark:text-green-400">
                                                    Successfully Imported
                                                </div>
                                            </div>
                                            <div className="text-center p-4 bg-amber-500/10 rounded-lg">
                                                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                                                    {importResults.ignored ?? 0}
                                                </div>
                                                <div className="text-sm text-amber-600 dark:text-amber-400">
                                                    Duplicates Ignored
                                                </div>
                                            </div>
                                            <div className="text-center p-4 bg-red-500/10 rounded-lg">
                                                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                                                    {importResults.failed}
                                                </div>
                                                <div className="text-sm text-red-600 dark:text-red-400">
                                                    Failed
                                                </div>
                                            </div>
                                        </div>

                                        {/* Success Rate */}
                                        {importResults.total_processed > 0 && (
                                            <div className="text-center">
                                                <div className="text-lg font-medium">
                                                    New leads imported:{' '}
                                                    {Math.round(
                                                        (importResults.imported /
                                                            importResults.total_processed) *
                                                            100
                                                    )}
                                                    % of rows
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Detailed Results */}
                            {(importResults.ignored ?? 0) > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-amber-600">
                                            <AlertTriangle className="h-5 w-5" />
                                            Duplicate Leads Ignored ({importResults.ignored})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            <p className="text-amber-800 dark:text-amber-300">
                                                These import rows matched an existing lead by email
                                                or phone. The existing lead was not modified.
                                            </p>
                                            <div className="bg-amber-500/10 p-4 rounded-lg max-h-64 overflow-y-auto">
                                                {(importResults.ignored_duplicates ?? []).map(
                                                    (duplicate, index) => (
                                                        <div
                                                            key={index}
                                                            className="text-amber-800 dark:text-amber-300 text-sm mb-2 last:mb-0"
                                                        >
                                                            • Row {duplicate.row}: duplicate lead
                                                            ignored
                                                            {duplicate.matched_by
                                                                ? ` (matched by ${duplicate.matched_by.replace(/_/g, ' ')})`
                                                                : ''}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {importResults.imported > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-green-600">
                                            <CheckCircle2 className="h-5 w-5" />
                                            Successfully Imported ({importResults.imported})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="bg-green-500/10 p-4 rounded-lg">
                                            <p className="text-green-800 dark:text-green-300">
                                                ✓ {importResults.imported} leads were successfully
                                                imported into the system.
                                            </p>
                                            {importResults.imported > 0 && (
                                                <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                                                    The leads list has been automatically refreshed
                                                    to show the new entries.
                                                </p>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Error Details */}
                            {importResults.failed > 0 && importResults.errors && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-red-600">
                                            <X className="h-5 w-5" />
                                            Import Errors ({importResults.failed})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            <p className="text-red-800 dark:text-red-300">
                                                The following rows could not be imported:
                                            </p>
                                            <div className="bg-red-500/10 p-4 rounded-lg max-h-64 overflow-y-auto">
                                                {importResults.errors.map(
                                                    (error: string, index: number) => (
                                                        <div
                                                            key={index}
                                                            className="text-red-800 dark:text-red-300 text-sm mb-2 last:mb-0"
                                                        >
                                                            • {error}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                            <div className="text-sm text-red-600 dark:text-red-400">
                                                <p className="font-medium">Common causes:</p>
                                                <ul className="list-disc list-inside mt-1 space-y-1">
                                                    <li>
                                                        Email or phone already registered as a
                                                        customer
                                                    </li>
                                                    <li>Missing required fields</li>
                                                    <li>Invalid data formats</li>
                                                    <li>Database constraints</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Recommendations */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Settings className="h-5 w-5" />
                                        Next Steps
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {importResults.failed > 0 ? (
                                            <>
                                                <p className="text-sm text-muted-foreground">
                                                    To resolve the import issues:
                                                </p>
                                                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                                                    <li>Review the error messages above</li>
                                                    <li>Fix the data in your CSV file</li>
                                                    <li>Remove duplicate email addresses</li>
                                                    <li>Ensure all required fields are filled</li>
                                                    <li>
                                                        Try importing again with the corrected data
                                                    </li>
                                                </ul>
                                            </>
                                        ) : (importResults.ignored ?? 0) > 0 ? (
                                            <div className="bg-amber-500/10 p-4 rounded-lg">
                                                <p className="text-amber-800 dark:text-amber-300">
                                                    Import finished. New leads were added; duplicate
                                                    rows were skipped and existing leads were left
                                                    unchanged.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="bg-green-500/10 p-4 rounded-lg">
                                                <p className="text-green-800 dark:text-green-300">
                                                    All rows were imported successfully. You can now
                                                    close this wizard and view your leads.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="flex items-center justify-between p-6 border-t bg-muted/50 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        {currentStep > 1 && (
                            <Button
                                variant="outline"
                                onClick={() => setCurrentStep(currentStep - 1)}
                                disabled={isAnalyzing || isImporting}
                            >
                                <ChevronLeft className="h-4 w-4 mr-2" />
                                Previous
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                resetWizard();
                                onClose();
                            }}
                            disabled={isAnalyzing || isImporting}
                        >
                            Cancel
                        </Button>

                        {currentStep === 2 && (
                            <>
                                <Button
                                    onClick={() => setCurrentStep(3)}
                                    disabled={!validation.isValid}
                                >
                                    Next <ChevronRight className="h-4 w-4 ml-2" />
                                </Button>
                                <Button
                                    onClick={handleImport}
                                    disabled={!validation.isValid || isImporting}
                                    className="ml-2"
                                >
                                    {isImporting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Importing...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-4 w-4 mr-2" />
                                            Import Now
                                        </>
                                    )}
                                </Button>
                            </>
                        )}

                        {currentStep === 3 && (
                            <Button onClick={() => setCurrentStep(4)}>
                                Next <ChevronRight className="h-4 w-4 ml-2" />
                            </Button>
                        )}

                        {currentStep === 4 && (
                            <Button
                                onClick={handleImport}
                                disabled={!validateMapping().isValid || isImporting}
                            >
                                {isImporting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Importing...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Start Import
                                    </>
                                )}
                            </Button>
                        )}

                        {currentStep === 5 && (
                            <div className="flex gap-2">
                                {importResults?.failed > 0 && (
                                    <Button variant="outline" onClick={() => setCurrentStep(2)}>
                                        Try Again
                                    </Button>
                                )}
                                <Button
                                    onClick={() => {
                                        resetWizard();
                                        onClose();
                                    }}
                                >
                                    Close Wizard
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedImportWizard;
